import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { useBranding } from '../context/BrandingContext';
import { usePlatformSettings } from '../context/PlatformSettingsContext';
import { notificationsAPI } from '../lib/api';
import { useBrowserNotifications } from '../hooks/useBrowserNotifications';
import { Button } from '../components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '../components/ui/dropdown-menu';
import { ScrollArea } from '../components/ui/scroll-area';
import { formatDateTime } from '../lib/utils';
import { Toaster } from '../components/ui/sonner';
import LanguageSwitcher from './LanguageSwitcher';
import { 
  LayoutDashboard, 
  ClipboardList, 
  Plus, 
  Bell,
  BellRing,
  User,
  LogOut,
  Wrench,
  Menu,
  X,
  Check,
  BarChart3,
  Settings,
  MapPin,
  Tag,
  Users,
  ChevronDown,
  Calendar,
  Clock,
  FileText,
  Crown,
  CreditCard,
  Building2,
  Trash2,
  Globe,
  Database,
  Package,
  CalendarCheck,
  Webhook
} from 'lucide-react';

const Layout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const { branding } = useBranding();
  const { platformSettings } = usePlatformSettings();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const isAdmin = user?.role === 'admin' || user?.role === 'supervisor';
  const isSuperAdmin = user?.role === 'super_admin';

  // Handle notification click - navigate to task
  const handleNotificationClick = useCallback((taskId) => {
    if (taskId) {
      navigate(`/tasks/${taskId}`);
    }
  }, [navigate]);

  // Browser push notifications hook
  const { requestPermission, isSupported } = useBrowserNotifications(
    notifications,
    handleNotificationClick
  );

  const fetchNotifications = useCallback(async () => {
    try {
      const response = await notificationsAPI.getAll();
      setNotifications(response.data);
      setUnreadCount(response.data.filter(n => !n.read).length);
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
    // Request notification permission on first load
    if (isSupported) {
      requestPermission();
    }
    // Refresh notifications every 30 seconds
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications, isSupported, requestPermission]);

  const handleMarkRead = async (id) => {
    try {
      await notificationsAPI.markRead(id);
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Failed to mark notification as read:', err);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await notificationsAPI.markAllRead();
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (err) {
      console.error('Failed to mark all as read:', err);
    }
  };

  const handleClearNotification = async (id) => {
    try {
      await notificationsAPI.delete(id);
      setNotifications(prev => prev.filter(n => n.id !== id));
      // Recalculate unread count
      setNotifications(prev => {
        setUnreadCount(prev.filter(n => !n.read).length);
        return prev;
      });
    } catch (err) {
      console.error('Failed to clear notification:', err);
    }
  };

  const handleClearAllNotifications = async () => {
    try {
      await notificationsAPI.clearAll();
      setNotifications([]);
      setUnreadCount(0);
    } catch (err) {
      console.error('Failed to clear notifications:', err);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navItems = [
    { path: '/dashboard', labelKey: 'common.dashboard', icon: LayoutDashboard, shortLabel: 'Dashboard' },
    { path: '/my-work', labelKey: 'common.myWork', icon: ClipboardList, showFor: 'technician', featureFlag: 'feature_technician_dashboard', shortLabel: 'My Work' },
    { path: '/tasks', labelKey: 'common.workOrders', icon: ClipboardList, shortLabel: 'Orders', hideFor: 'super_admin' },
    { path: '/calendar', labelKey: 'common.calendar', icon: Calendar, shortLabel: 'Calendar', hideFor: 'super_admin' },
    { path: '/time-log', labelKey: 'common.myTimeLog', icon: Clock, shortLabel: 'Time Log', hideFor: 'super_admin' },
    { path: '/reports', labelKey: 'common.reports', icon: BarChart3, shortLabel: 'Reports', hideFor: 'super_admin' },
    // Super Admin specific menu items
    { path: '/super-admin', labelKey: 'common.companies', icon: Building2, showFor: 'super_admin', shortLabel: 'Companies' },
    { path: '/super-admin?tab=billing', labelKey: 'common.billing', icon: CreditCard, showFor: 'super_admin', shortLabel: 'Billing' },
    { path: '/super-admin/backup', labelKey: 'common.backupRestore', icon: Database, showFor: 'super_admin', shortLabel: 'Backup' },
    { path: '/super-admin?tab=cleanup', labelKey: 'common.dataCleanup', icon: Trash2, showFor: 'super_admin', shortLabel: 'Cleanup' },
    { path: '/super-admin/settings', labelKey: 'common.platformSettings', icon: Settings, showFor: 'super_admin', shortLabel: 'Settings' },
  ];

  const adminNavItems = [
    { path: '/admin/users', labelKey: 'common.users', icon: User },
    { path: '/admin/locations', labelKey: 'common.locations', icon: MapPin },
    { path: '/admin/categories', labelKey: 'common.categories', icon: Tag },
    { path: '/admin/inventory', labelKey: 'common.inventory', icon: Package },
    { path: '/admin/purchase-orders', labelKey: 'common.purchaseOrders', icon: Package },
    { path: '/admin/pm-schedules', labelKey: 'common.pmSchedules', icon: CalendarCheck },
    { path: '/admin/webhooks', labelKey: 'common.webhooks', icon: Webhook },
    { path: '/time-reports', labelKey: 'common.timeReports', icon: FileText },
    { path: '/billing', labelKey: 'common.billing', icon: CreditCard },
    { path: '/admin/settings', labelKey: 'common.settings', icon: Settings },
  ];
  
  // Super admin only items
  const superAdminNavItems = [
    { path: '/admin/landing-editor', labelKey: 'common.landingPage', icon: Globe },
  ];

  // Filter nav items based on user role and feature flags
  const filteredNavItems = navItems.filter(item => {
    if (item.showFor === 'technician' && user?.role !== 'technician') return false;
    if (item.showFor === 'super_admin' && user?.role !== 'super_admin') return false;
    if (item.hideFor === 'technician' && user?.role === 'technician') return false;
    if (item.hideFor === 'super_admin' && user?.role === 'super_admin') return false;
    // Check feature flags from branding settings
    if (item.featureFlag && branding && branding[item.featureFlag] === false) return false;
    return true;
  });

  return (
    <div className="min-h-screen bg-slate-950">
      {/* Top Header */}
      <header className="glass-header sticky top-0 z-40 h-16 flex items-center px-4 md:px-6 bg-slate-900/90 border-b border-slate-700">
        <div className="flex items-center justify-between w-full max-w-7xl mx-auto">
          {/* Logo */}
          <Link to="/dashboard" className="flex items-center gap-3" data-testid="logo-link">
            {isSuperAdmin ? (
              // Super Admin - Use platform settings
              <>
                {platformSettings.logo_url ? (
                  <img 
                    src={platformSettings.logo_url} 
                    alt={platformSettings.platform_name} 
                    className="h-10 w-auto max-w-[150px] object-contain"
                  />
                ) : (
                  <span 
                    className="text-xl font-bold uppercase tracking-wider bg-gradient-to-r from-amber-400 to-cyan-400 bg-clip-text text-transparent"
                  >
                    {platformSettings.platform_name}
                  </span>
                )}
              </>
            ) : (
              // Regular users - Use company branding
              <>
                {branding.logo_url ? (
                  <img 
                    src={branding.logo_url} 
                    alt={branding.logo_text || branding.app_name} 
                    className="w-10 h-10 rounded-sm object-contain"
                  />
                ) : (
                  <div 
                    className="w-10 h-10 rounded-sm flex items-center justify-center bg-amber-600"
                    style={{ backgroundColor: branding.primary_color }}
                  >
                    <Wrench className="w-5 h-5 text-white" strokeWidth={2} />
                  </div>
                )}
                {branding.show_logo_text && (
                  <span 
                    className="text-xl font-bold uppercase tracking-wider text-white hidden sm:block"
                    style={{ fontFamily: branding.font_family }}
                  >
                    {branding.app_name}
                  </span>
                )}
              </>
            )}
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1">
            {filteredNavItems.map((item) => {
              const Icon = item.icon;
              // Handle paths with query strings for active state
              const [itemPath, itemQuery] = item.path.split('?');
              const isActive = location.pathname === itemPath && 
                (!itemQuery || location.search === `?${itemQuery}`) ||
                (item.path === '/tasks' && location.pathname.startsWith('/tasks/') && location.pathname !== '/tasks/new');
              
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-sm font-medium text-sm transition-colors ${
                    isActive 
                      ? 'bg-amber-500/20 text-amber-400' 
                      : 'text-slate-400 hover:text-white hover:bg-slate-800/50'
                  }`}
                  data-testid={`nav-${item.path.replace(/\//g, '-').replace(/\?/g, '-').slice(1)}`}
                >
                  <Icon className="w-4 h-4" />
                  <span className="hidden xl:inline">{t(item.labelKey)}</span>
                  <span className="xl:hidden">{item.shortLabel}</span>
                </Link>
              );
            })}
            
            {/* Super Admin Navigation Items */}
            {user?.role === 'super_admin' && superAdminNavItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-sm font-medium text-sm transition-colors ${
                    isActive 
                      ? 'bg-amber-500/20 text-amber-400' 
                      : 'text-slate-400 hover:text-white hover:bg-slate-800/50'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span className="hidden xl:inline">{t(item.labelKey)}</span>
                  <span className="xl:hidden">{item.shortLabel}</span>
                </Link>
              );
            })}
            
            {/* Admin Dropdown - Only for company admins, NOT super admin */}
            {isAdmin && user?.role !== 'super_admin' && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="ghost" 
                    className={`flex items-center gap-1.5 px-3 py-2 rounded-sm font-medium text-sm transition-colors ${
                      location.pathname.startsWith('/admin') || location.pathname === '/time-reports'
                        ? 'bg-amber-500/20 text-amber-400' 
                        : 'text-slate-400 hover:text-white hover:bg-slate-800/50'
                    }`}
                    data-testid="admin-menu-btn"
                  >
                    <Users className="w-4 h-4" />
                    <span className="hidden xl:inline">{t('common.admin')}</span>
                    <span className="xl:hidden">Admin</span>
                    <ChevronDown className="w-3 h-3" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="bg-slate-900 border-slate-700 w-48">
                  {adminNavItems.map((item) => {
                    // Skip if demo user and item is billing
                    if (user?.role === 'demo' && item.path === '/billing') {
                      return null;
                    }
                    
                    const Icon = item.icon;
                    return (
                      <DropdownMenuItem 
                        key={item.path}
                        onClick={() => navigate(item.path)}
                        className="cursor-pointer"
                        data-testid={`admin-nav-${item.path.split('/').pop()}`}
                      >
                        <Icon className="w-4 h-4 mr-2" />
                        {t(item.labelKey)}
                      </DropdownMenuItem>
                    );
                  })}
                  
                  {/* Super Admin Only Items */}
                  {user?.role === 'super_admin' && superAdminNavItems.map((item) => {
                    const Icon = item.icon;
                    return (
                      <DropdownMenuItem 
                        key={item.path}
                        onClick={() => navigate(item.path)}
                        className="cursor-pointer"
                        data-testid={`admin-nav-${item.path.split('/').pop()}`}
                      >
                        <Icon className="w-4 h-4 mr-2" />
                        {t(item.labelKey)}
                      </DropdownMenuItem>
                    );
                  })}
                </DropdownMenuContent>
              </DropdownMenu>
            )}

            {/* New Work Order Button */}
            {/* New Work Order Button - Hidden for Super Admin */}
            {user?.role !== 'super_admin' && (
              <Button
                onClick={() => navigate('/tasks/new')}
                className="bg-amber-500 hover:bg-amber-600 text-white px-3 py-2 h-auto text-sm font-medium"
                data-testid="nav-new-work-order"
              >
                <Plus className="w-4 h-4 mr-1" />
                <span className="hidden xl:inline">New Work Order</span>
                <span className="xl:hidden">New</span>
              </Button>
            )}
          </nav>

          {/* Right Side */}
          <div className="flex items-center gap-2">
            {/* Notifications */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="ghost" 
                  className="relative p-2 text-slate-400 hover:text-white hover:bg-slate-800/50"
                  data-testid="notifications-btn"
                >
                  <Bell className="w-5 h-5" />
                  {unreadCount > 0 && (
                    <span className="absolute top-1 right-1 w-4 h-4 bg-amber-500 text-white text-xs rounded-full flex items-center justify-center notification-badge">
                      {unreadCount}
                    </span>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent 
                align="end" 
                className="w-80 bg-slate-900 border-slate-700"
              >
                <div className="p-3 border-b border-slate-700 flex items-center justify-between">
                  <h3 className="font-semibold text-white">{t('notifications.title')}</h3>
                  {notifications.length > 0 && (
                    <div className="flex gap-1">
                      {unreadCount > 0 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={handleMarkAllRead}
                          className="text-xs text-slate-400 hover:text-amber-400 px-2 py-1 h-auto"
                          data-testid="mark-all-read-btn"
                        >
                          {t('notifications.markAllRead')}
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleClearAllNotifications}
                        className="text-xs text-slate-400 hover:text-red-400 px-2 py-1 h-auto"
                        data-testid="clear-all-btn"
                      >
                        {t('notifications.clearAll')}
                      </Button>
                    </div>
                  )}
                </div>
                <ScrollArea className="h-[300px]">
                  {notifications.length > 0 ? (
                    notifications.map((notification) => (
                      <div 
                        key={notification.id}
                        className={`p-3 border-b border-slate-700/50 ${!notification.read ? 'bg-slate-700/30' : ''}`}
                        data-testid={`notification-${notification.id}`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div 
                            className="flex-1 min-w-0 cursor-pointer hover:opacity-80 transition-opacity"
                            onClick={() => {
                              if (notification.task_id) {
                                handleNotificationClick(notification.task_id);
                                handleMarkRead(notification.id);
                              }
                            }}
                          >
                            <p className="text-sm text-white font-medium truncate hover:text-amber-400 transition-colors">{notification.task_title}</p>
                            <p className="text-sm text-slate-400 mt-1">{notification.message}</p>
                            <p className="text-xs text-slate-500 mt-1">{formatDateTime(notification.created_at)}</p>
                          </div>
                          <div className="flex items-center gap-1">
                            {!notification.read && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleMarkRead(notification.id);
                                }}
                                className="p-1 h-auto text-slate-400 hover:text-amber-400"
                                title="Mark as read"
                              >
                                <Check className="w-4 h-4" />
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleClearNotification(notification.id);
                              }}
                              className="p-1 h-auto text-slate-400 hover:text-red-400"
                              title="Remove"
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="p-8 text-center text-slate-400">
                      <Bell className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p>{t('notifications.noNotifications')}</p>
                    </div>
                  )}
                </ScrollArea>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* User Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="ghost" 
                  className="flex items-center gap-2 p-2 text-slate-400 hover:text-white hover:bg-slate-700/50"
                  data-testid="user-menu-btn"
                >
                  <div className="w-8 h-8 bg-slate-700 rounded-full flex items-center justify-center">
                    <User className="w-4 h-4" />
                  </div>
                  <span className="hidden sm:block text-sm">{user?.name}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-slate-900 border-slate-700 w-48">
                <div className="p-2 border-b border-slate-700">
                  <p className="text-sm text-white font-medium">{user?.name}</p>
                  <p className="text-xs text-slate-400 truncate">{user?.email}</p>
                  <p className="text-xs text-amber-400 uppercase mt-1">{user?.role}</p>
                </div>
                <DropdownMenuItem 
                  onClick={() => navigate('/profile')}
                  className="cursor-pointer"
                  data-testid="profile-link"
                >
                  <User className="w-4 h-4 mr-2" />
                  {t('common.profile')}
                </DropdownMenuItem>
                {isAdmin && (
                  <>
                    <DropdownMenuItem 
                      onClick={() => navigate('/admin/settings')}
                      className="cursor-pointer"
                      data-testid="settings-link"
                    >
                      <Settings className="w-4 h-4 mr-2" />
                      {t('common.settings')}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator className="bg-slate-700" />
                  </>
                )}
                <DropdownMenuItem 
                  onClick={handleLogout}
                  className="text-red-400 focus:text-red-400 cursor-pointer"
                  data-testid="logout-btn"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  {t('common.logout')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Language Switcher */}
            <LanguageSwitcher />

            {/* Mobile Menu Toggle */}
            <Button
              variant="ghost"
              className="lg:hidden p-2 text-slate-400"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
          </div>
        </div>
      </header>

      {/* Mobile Navigation */}
      {mobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 top-16 bg-slate-950/95 backdrop-blur-lg z-30">
          <nav className="flex flex-col p-4 gap-2">
            {/* New Work Order Button - Mobile - Hidden for super admin */}
            {user?.role !== 'super_admin' && (
              <Link
                to="/tasks/new"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center justify-center gap-2 px-4 py-3 rounded-sm font-medium bg-amber-500 text-white hover:bg-amber-600 transition-colors mb-2"
              >
                <Plus className="w-5 h-5" />
                New Work Order
              </Link>
            )}

            {filteredNavItems.map((item) => {
              const Icon = item.icon;
              const [itemPath, itemQuery] = item.path.split('?');
              const isActive = location.pathname === itemPath && 
                (!itemQuery || location.search === `?${itemQuery}`);
              
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-sm font-medium transition-colors ${
                    isActive 
                      ? 'bg-amber-500/20 text-amber-400' 
                      : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  {t(item.labelKey)}
                </Link>
              );
            })}
            
            {/* Admin Links for Mobile - Hidden for super admin */}
            {isAdmin && user?.role !== 'super_admin' && (
              <>
                <div className="border-t border-slate-700 my-2 pt-2">
                  <p className="text-slate-400 text-xs uppercase tracking-wider px-4 mb-2">{t('common.admin')}</p>
                </div>
                {adminNavItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = location.pathname === item.path;
                  
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => setMobileMenuOpen(false)}
                      className={`flex items-center gap-3 px-4 py-3 rounded-sm font-medium transition-colors ${
                        isActive 
                          ? 'bg-amber-500/20 text-amber-400' 
                          : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
                      }`}
                    >
                      <Icon className="w-5 h-5" />
                      {t(item.labelKey)}
                    </Link>
                  );
                })}
              </>
            )}
          </nav>
        </div>
      )}

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 md:px-8 py-6 md:py-8 pb-24 md:pb-8">
        {children}
      </main>

      {/* Mobile Bottom Nav */}
      <nav className="mobile-nav md:hidden">
        {filteredNavItems.slice(0, 3).map((item) => {
          const Icon = item.icon;
          const isActive = location.pathname === item.path || 
            (item.path === '/tasks' && location.pathname.startsWith('/tasks/') && location.pathname !== '/tasks/new');
          
          return (
            <Link
              key={item.path}
              to={item.path}
              className={`flex flex-col items-center gap-1 px-4 py-2 ${
                isActive ? 'text-amber-500' : 'text-slate-400'
              }`}
              data-testid={`mobile-nav-${item.path.replace(/\//g, '-').slice(1)}`}
            >
              <Icon className="w-5 h-5" />
              <span className="text-xs">{t(item.labelKey)}</span>
            </Link>
          );
        })}
      </nav>

      {/* Toast Container */}
      <Toaster position="top-right" richColors />
    </div>
  );
};

export default Layout;
